import { Component, OnInit, OnDestroy, HostListener, ViewChild, inject } from '@angular/core';
import { AppContextService } from '@drc-eca/eca-components-lib';
import { IClient, IClientServiceMap, IService, IIssue, IIssueDetail, IIssueClientServiceMap, IStatusType } from '../../model/common.model';
import { ClientStatusService } from './client-status.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HasAppViewPermissionGuard } from '../../guards/has-app-view-permission.guard';
import { StatusDialogComponent, ServiceDetailDialogComponent } from '../status-dialog/status-dialog.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { Subject, Subscription, Observable, timer } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { SharingService } from '../sharing/sharingservice.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EcaSnackbarAlertComponent, EcaSnackbarAlertConfig } from '@drc-eca/eca-components-lib';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';


@Component({
  selector: 'app-status',
  standalone: true,
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDivider,
    MatButtonModule
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
  ]
})
export class StatusComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private clientStatusSvc = inject(ClientStatusService);
  private appContextSvc = inject(AppContextService);
  private hasAppViewPermissionGuard = inject(HasAppViewPermissionGuard);
  dialog = inject(MatDialog);
  private sharingService = inject(SharingService);
  snackBar = inject(MatSnackBar);

  expandedElement: any | null;
  servicesWithIssuesString: string = '';
  clientCode: string;
  client: IClient;
  clientServices: IClientServiceMap[] = [];
  serviceList: IService[] = [];
  issueList: IIssue[] = [];
  issueClientServiceMapList: IIssueClientServiceMap[] = [];
  statusHeader: string = null;
  hasUpdatePermission: boolean = false;
  serviceStatusList: IStatusType[] = [];
  dialogRef: MatDialogRef<StatusDialogComponent>;
  descRef: MatDialogRef<ServiceDetailDialogComponent>;
  unsubscribe$: Subject<void> = new Subject();
  timerSubscription: Subscription;
  _userActionOccured: Subject<void> = new Subject();
  lastRefreshed: any;
  clientNotFound: boolean = false;
  statusTableColumns: string[] = ['serviceAlias', 'serviceStatus'];
  statusTableColumnsWithExpand = [...this.statusTableColumns, 'expand'];
  statusTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  @ViewChild('sort', { static: true }) sort: MatSort;

  constructor() { }

  ngOnInit() {
    const token = sessionStorage.getItem('ngStorage-ecatoken');
    this.clientNotFound = false;
    this.statusHeader = 'operational';
    if (this.route.snapshot.paramMap.get('clientCode')) {
      this.clientCode = this.route.snapshot.paramMap.get('clientCode');
      const test = this.appContextSvc.setSelectedProductCode(this.clientCode);

      // Only check permissions if user is authenticated (has valid token in session)
      const hasValidToken = token != null && token !== 'null' && token !== '';
      if (hasValidToken) {
        this.hasAppViewPermissionGuard.userHasPermission().subscribe(hasPermission => {
          this.hasUpdatePermission = hasPermission;
        }, error => {
          // If permission check fails, just set to false (public view)
          this.hasUpdatePermission = false;
        });
      } else {
        // No valid token means public access
        this.hasUpdatePermission = false;
      }

      this.getIssueClientServiceMapping();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async getIssueClientServiceMapping() {
    this.lastRefreshed = new Date();
    const data: any = await this.clientStatusSvc.doGet(`clientstatus/${this.clientCode.toUpperCase()}/active`).toPromise();

    // Do this once in order to avoid change-detection constantly checking for new updates
    data.outages.forEach((outage) => {
      outage.outageUpdates.forEach(update => {
        update.color = this.getClass(update.status);
      })
    });

    this.serviceStatusList = (data.statusTypes).sort((a, b) => b.statusPrecedence - a.statusPrecedence);
    this.client = {
      code: this.clientCode,
      title: data.title,
      description: data.description
    };
    this.sharingService.setData(this.client.title);
    this.issueList = data.outages || [];
    const services = data.services || [];
    this.issueList.forEach((issue: any) => {
      issue.outageUpdates = this.clientStatusSvc.sortArray(issue.outageUpdates || [], 'createTime', 'desc');
    });
    const dataSource = services.map((service: any) => {
      return {
        serviceAlias: service.alias,
        serviceStatus: service.severity,
        serviceStatusColor: this.getClass(service.severity),
        serviceDescription: service.description,
        issues: this.issueList.filter((issue: any) => issue.services.includes(service.primaryKeyPart))
      };
    });
    this.servicesWithIssuesString = dataSource.filter((ds: any) => ds.serviceStatus !== this.serviceStatusList[this.serviceStatusList.length - 1].statusName && ds.issues.length).map((svc: any) => svc.serviceAlias).join(', ');
    this.statusTableDataSource = new MatTableDataSource<any>(dataSource);
    this.statusTableDataSource.sort = this.sort;

    this.setStatus();
    this.checkSessionIdle();
    this.changeStatusStrings();
  }

  getIssue = (issueMap: IIssueClientServiceMap) => {
    return new Promise((resolve, reject) => {
      this.clientStatusSvc.doGet(`issue/${issueMap.issueId}`).subscribe(data => {
        this.clientStatusSvc.doGet(`issuedetails?issueId=${issueMap.issueId}`).subscribe(dataDetails => {
          let issueUpdates: IIssueDetail[] = [];
          issueUpdates = this.clientStatusSvc.sortArray(<any[]>dataDetails['Items'], 'createtime', 'desc');
          data['Item']['updates'] = issueUpdates;
          resolve(data);
        });
      }, error => {
        console.log(error);
        reject(error);
      });
    });
  }

  getClass = (name: string) => {
    const status = this.serviceStatusList.findIndex(s => s.statusName == name);
    console.log(name, status)
    if (status > -1) {
      return this.serviceStatusList[status].statusColorCode;
    } else {
      return this.clientStatusSvc.getClass(name);
    }
  }

  setStatus = () => {
    this.statusHeader = '';
    if (this.issueList.length) {
      let hPrecedence = 1;
      let st: IStatusType = {} as any;
      for (let i = 0; i < this.serviceStatusList.length; i++) {
        const statusType = this.serviceStatusList[i];
        if (this.issueList.find((outage: any) => outage.severity == statusType.statusName)) {
          if (statusType.statusPrecedence >= hPrecedence) {
            st = { ...statusType };
            hPrecedence = statusType.statusPrecedence
          }
        }
      }
      this.statusHeader = this.servicesWithIssuesString;
      // if (this.issueList.find((outage: any) => outage.severity == 'Major Outage')) {
      //   this.statusHeader = 'majoroutage';
      // } else if (this.issueList.find((outage: any) => outage.severity == 'Partial Outage')) {
      //   this.statusHeader = 'partialoutage';
      // } else if (this.issueList.find((outage: any) => outage.severity == 'Degraded Performance')) {
      //   this.statusHeader = 'degraded';
      // } else {
      //   this.statusHeader = 'operational';
      // }
    }
  }

  openissueDetailDialog(issue: IIssue) {
    this.dialogRef = this.dialog.open(StatusDialogComponent, {
      height: '95vh',
      width: '90%',
      disableClose: true,
      data: {
        client: this.client,
        issue: issue,
        issueClientServiceMapList: this.issueClientServiceMapList,
        serviceList: this.serviceList,
        clientServices: this.clientServices
      }

    });
    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {

      }
      this.dialogRef = null;
    });
  }

  get userActionOccured(): Observable<void> {
    return this._userActionOccured.asObservable();
  }

  notifyUserAction() {
    this._userActionOccured.next();
  }

  resetTimer(endTime: number = 1) {
    const interval = 1000 * 60 * 15;
    const duration = endTime * 60 * 60; // seconds

    this.timerSubscription = timer(0, interval).pipe(
      take(duration)
    ).subscribe(value => {
      if ((value * interval) < (duration * 1000)) {
        const now = new Date();
        if (this.lastRefreshed && !(now.getTime() - this.lastRefreshed.getTime() <= interval)) {
          this.getIssueClientServiceMapping();
        }

      } else {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
      }

    },
      err => { },
      () => {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
      }
    );
  }

  checkSessionIdle = () => {
    this.resetTimer(1);
    this.userActionOccured.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(() => {
      if (this.timerSubscription) {
        this.timerSubscription.unsubscribe();
      }
      this.resetTimer(1);
    });
  }

  @HostListener('document:keyup', ['$event'])
  @HostListener('document:click', ['$event'])
  @HostListener('document:wheel', ['$event'])
  resetSessionIdle() {
    this.notifyUserAction();
  }

  logOut() {
    sessionStorage.clear();
    this.router.navigateByUrl('/status/login');
  }

  changeStatusStrings() {
    // this.status = {
    //   operational: 'All Systems Operational',
    //   partialoutage: 'Partial Outage on ' + this.servicesWithIssuesString,
    //   majoroutage: 'Complete Outage on ' + this.servicesWithIssuesString,
    //   degraded: 'Users may experience Degraded Performance on ' + this.servicesWithIssuesString
    // };
    if (this.servicesWithIssuesString && this.servicesWithIssuesString.length > 1) {
      this.openBadSnackBar();
    } else {
      this.openGoodSnackBar();
    }
  }

  openBadSnackBar() {
    const config = new EcaSnackbarAlertConfig(this.statusHeader, 'negative');
    this.snackBar.openFromComponent(EcaSnackbarAlertComponent, config);
  }
  openGoodSnackBar() {
    const config = new EcaSnackbarAlertConfig('All Systems Operational', 'positive');
    this.snackBar.openFromComponent(EcaSnackbarAlertComponent, config);
  }
}
